import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Alert, Box, Button, LinearProgress, Paper, Typography} from '@mui/material';
import {usePermissions} from '../../shared/permissions/PermissionContext';
import {
  acceptanceOf,
  acceptLegalDocuments,
  getLegalStatus,
  LegalDocumentsDto,
  LegalRefusalError,
  TERMS_OUTDATED,
} from '../../shared/services/legal.service';
import {signOutUser} from '../../shared/services/auth.service';
import {logError} from '../../shared/services/logger.service';
import LegalAgreementCheckbox from './LegalAgreementCheckbox';

/**
 * SQ-419: nobody uses the app without agreeing to the current Terms of
 * Service and Privacy Policy. users/me says when someone has not (an
 * invited member's first sign-in, or either document changed since they
 * last agreed); until they agree here, this stands in for every signed-in
 * page. The owner who signed up agreed on the signup form and never sees it.
 */
const LegalAcceptanceGate: React.FC<{children: React.ReactNode}> = ({children}) => {
  const {me, refresh} = usePermissions();
  const navigate = useNavigate();
  const required = me?.legalAcceptanceRequired === true;
  const [documents, setDocuments] = useState<LegalDocumentsDto | null>(null);
  const [returning, setReturning] = useState(false);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!required) return;
    let cancelled = false;
    getLegalStatus()
      .then((status) => {
        if (cancelled) return;
        setDocuments(status.documents);
        setReturning(status.lastAcceptedAt != null);
      })
      .catch((e) => {
        logError('Failed to load legal status', {}, e as Error);
        if (!cancelled) setError('Could not load the Terms of Service. Reload the page to try again.');
      });
    return () => { cancelled = true; };
  }, [required]);

  if (!required) return <>{children}</>;

  const accept = async () => {
    if (!documents) return;
    setSubmitting(true);
    setError(null);
    try {
      await acceptLegalDocuments(acceptanceOf(documents));
      await refresh();
    } catch (e) {
      if (e instanceof LegalRefusalError && e.code === TERMS_OUTDATED) {
        // A document changed while this page was open: show the new one.
        const status = await getLegalStatus().catch(() => null);
        if (status) setDocuments(status.documents);
        setChecked(false);
      }
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const signOut = async () => {
    await signOutUser().catch(() => undefined);
    navigate('/login');
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" p={2}>
      <Paper elevation={3} sx={{p: 4, width: '100%', maxWidth: 440}} data-testid="legal-acceptance">
        <Typography variant="h5" align="center" gutterBottom>
          {returning ? 'Our terms have changed' : 'Before you start'}
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{mb: 2}}>
          {returning
            ? 'Please read and accept the updated Terms of Service and Privacy Policy to keep using SavaQuan.'
            : 'Please read and accept the Terms of Service and Privacy Policy to use SavaQuan.'}
        </Typography>
        {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
        {!documents && !error && <LinearProgress sx={{mb: 2}} />}
        <LegalAgreementCheckbox documents={documents} checked={checked} onChange={setChecked} disabled={submitting} />
        <Button
          variant="contained"
          fullWidth
          sx={{mt: 2}}
          disabled={!checked || !documents || submitting}
          onClick={accept}
        >
          Continue
        </Button>
        <Button fullWidth sx={{mt: 1}} disabled={submitting} onClick={signOut}>
          Sign out
        </Button>
      </Paper>
    </Box>
  );
};

export default LegalAcceptanceGate;
